import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, message } from 'antd';
import {
  UserOutlined,
  ShoppingCartOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  FileTextOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { SalesProvider, useSales } from '../SalesManager/SalesContext';
import { SaleData } from '../SalesManager/types';
import CustomerInfoStep from './steps/CustomerInfoStep';
import ProductSelectionStep from './steps/ProductSelectionStep';
import RecommendationsStep from './steps/RecommendationsStep';
import ReviewStep from './steps/ReviewStep';
import { salesAnalytics } from '../SalesManager/utils/analytics';
import ProductResourcesViewer from '../SalesManager/resources/ProductResourcesViewer';
import apiService, { Lead } from '../../services/api';
import { salesApi } from '../../services/salesApi';
import { useCognitoAuth } from '../../contexts/CognitoAuthContext';
import DuplicateLoader from './components/DuplicateLoader';
import './VendaModal.css';

// Duplicate lead info
export interface DuplicateInfo {
  found: boolean;
  existingLead?: Lead;
  matchedBy?: 'phone' | 'email' | 'both';
}

// Operator/Wizard types for all business areas
export type OperatorType = 
  // Telecomunicações
  | 'nos' 
  | 'meo' 
  | 'vodafone' 
  | 'altice'
  // Segurança
  | 'prosegur' 
  | 'nos_securitas'
  // Energia / Solar
  | 'energy';

// Customer data to pass when operator is selected
export interface OperatorCustomerData {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface VendaModalProps {
  visible: boolean;
  onClose: () => void;
  onComplete?: (saleData: SaleData) => void;
  onOperatorSelected?: (operator: OperatorType, customerData: OperatorCustomerData) => void;
  leadId?: number;
  clientId?: number;
  clientData?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  skipCustomerStep?: boolean; // Skip the customer info step when opening from an existing lead
}

interface StepConfig {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const VendaModalContent: React.FC<VendaModalProps> = ({
  visible,
  onClose,
  onComplete,
  onOperatorSelected,
  leadId,
  clientId,
  clientData,
  skipCustomerStep = false
}) => {
  const { fetchProducts, fetchSections, sections } = useSales();
  const { user } = useCognitoAuth();
  const initialStep = skipCustomerStep && clientData ? 1 : 0;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo>({ found: false });
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [saleData, setSaleData] = useState<Partial<SaleData>>({
    customerInfo: { name: '', email: '', phone: '', address: '' },
    businessArea: undefined,
    selectedProducts: [],
    additionalProducts: [],
    totalCommission: 0,
    leadId,
    clientId
  });

  const [resourcesVisible, setResourcesVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adjustedCommissions, setAdjustedCommissions] = useState<{ [businessArea: string]: any }>({});
  const [currentInstalados, setCurrentInstalados] = useState<number>(0);
  const [currentInstaladosSeguranca, setCurrentInstaladosSeguranca] = useState<number>(0);
  const [segurancaProductsSold, setSegurancaProductsSold] = useState<number>(0);
  const startTimeRef = useRef<number>(Date.now());

  const steps: StepConfig[] = [
    { key: 'cliente', title: 'Cliente', description: 'Dados pessoais', icon: <UserOutlined /> },
    { key: 'produtos', title: 'Produtos', description: 'Seleção', icon: <ShoppingCartOutlined /> },
    { key: 'extras', title: 'Extras', description: 'Recomendações', icon: <GiftOutlined /> },
    { key: 'revisao', title: 'Revisão', description: 'Confirmar', icon: <CheckCircleOutlined /> }
  ];

  useEffect(() => {
    if (visible) {
      fetchProducts();
      fetchSections();
      startTimeRef.current = Date.now();
      salesAnalytics.trackSaleStarted();
      
      // Fetch adjusted commissions for Telecomunicação and Segurança
      const fetchAdjustedCommissions = async () => {
        try {
          // Fetch Telecomunicação
          const telecomData = await salesApi.getAdjustedCommissions('telecomunicacao');
          setAdjustedCommissions(prev => ({ ...prev, telecomunicacao: telecomData }));
          setCurrentInstalados(telecomData.current_instalados);
          
          // Fetch Segurança
          const segurancaData = await salesApi.getAdjustedCommissions('seguranca');
          setAdjustedCommissions(prev => ({ ...prev, seguranca: segurancaData }));
          setCurrentInstaladosSeguranca(segurancaData.current_instalados);
          
          // Fetch Segurança products sold count
          const segurancaProductsData = await salesApi.getCurrentMonthSegurancaProductsSold();
          setSegurancaProductsSold(segurancaProductsData.count);
        } catch (error) {
          console.error('Error fetching adjusted commissions:', error);
          // Continue without adjusted commissions if there's an error
        }
      };
      fetchAdjustedCommissions();
      
      // Refresh instalados count every 30 seconds
      const interval = setInterval(fetchAdjustedCommissions, 30000);
      
      // Set initial step based on skipCustomerStep
      const startStep = skipCustomerStep && clientData ? 1 : 0;
      setCurrentStep(startStep);
      
      return () => clearInterval(interval);

      if (clientData) {
        const data = clientData;
        setSaleData(prev => ({
          ...prev,
          customerInfo: {
            name: data?.name ?? '',
            email: data?.email ?? '',
            phone: data?.phone ?? '',
            address: data?.address ?? ''
          },
          leadId
        }));
      }
    } else {
      setCurrentStep(0);
      setSaleData({
        customerInfo: { name: '', email: '', phone: '', address: '' },
        businessArea: undefined,
        selectedProducts: [],
        additionalProducts: [],
        totalCommission: 0,
        leadId,
        clientId
      });
      setResourcesVisible(false);
      setDuplicateInfo({ found: false }); // Reset duplicate info
      setAdjustedCommissions({});
      setCurrentInstalados(0);
      setCurrentInstaladosSeguranca(0);
      setSegurancaProductsSold(0);
    }
  }, [visible, clientData, leadId, clientId, fetchProducts, fetchSections]);

  const updateData = useCallback((newData: Partial<SaleData>) => {
    setSaleData(prev => {
      const updated = { ...prev, ...newData };
      const allProducts = [
        ...(updated.selectedProducts || []),
        ...(updated.additionalProducts || [])
      ];
      // Use the commission value from the product (which may already be adjusted)
      const totalCommission = allProducts.reduce(
        (sum, item) => sum + ((item.product.commission || 0) * item.quantity),
        0
      );
      return { ...updated, totalCommission };
    });
  }, []);

  // Check for duplicate leads by phone or email
  const checkForDuplicates = async (phone?: string, email?: string): Promise<DuplicateInfo> => {
    if (!phone && !email) return { found: false };
    
    try {
      setCheckingDuplicates(true);
      // Get all leads and check for duplicates
      const leads = await apiService.getLeads();
      
      let matchedLead: Lead | undefined;
      let matchedBy: 'phone' | 'email' | 'both' | undefined;
      
      for (const lead of leads) {
        const phoneMatch = phone && lead.phone && lead.phone.replace(/\s/g, '') === phone.replace(/\s/g, '');
        const emailMatch = email && lead.email && lead.email.toLowerCase() === email.toLowerCase();
        
        if (phoneMatch && emailMatch) {
          matchedLead = lead;
          matchedBy = 'both';
          break;
        } else if (phoneMatch) {
          matchedLead = lead;
          matchedBy = 'phone';
        } else if (emailMatch && !matchedLead) {
          matchedLead = lead;
          matchedBy = 'email';
        }
      }
      
      return {
        found: !!matchedLead,
        existingLead: matchedLead,
        matchedBy
      };
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return { found: false };
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      if (!saleData.customerInfo?.name || !saleData.customerInfo?.phone) {
        message.warning('Nome e Telefone são obrigatórios');
        return;
      }
      
      // Check for duplicates when leaving customer info step
      const duplicates = await checkForDuplicates(
        saleData.customerInfo.phone,
        saleData.customerInfo.email
      );
      setDuplicateInfo(duplicates);
      
    } else if (currentStep === 1) {
      if (!saleData.selectedProducts || saleData.selectedProducts.length === 0) {
        message.warning('Selecione pelo menos um produto');
        return;
      }
    }

    salesAnalytics.trackSaleStepCompleted(currentStep, steps[currentStep].title);

    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      await handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Helper to detect operator/wizard from section name
  const detectOperatorFromSectionName = (sectionName: string, businessArea?: string): OperatorType | null => {
    const normalizedName = sectionName.toLowerCase().trim();
    const normalizedArea = (businessArea || '').toLowerCase().trim();
    
    // Telecomunicações
    if (normalizedName.includes('nos') && !normalizedName.includes('securitas')) return 'nos';
    if (normalizedName.includes('meo')) return 'meo';
    if (normalizedName.includes('vodafone')) return 'vodafone';
    if (normalizedName.includes('altice')) return 'altice';
    
    // Segurança
    if (normalizedName.includes('prosegur')) return 'prosegur';
    if (normalizedName.includes('nos') && normalizedName.includes('securitas')) return 'nos_securitas';
    if (normalizedName.includes('securitas')) return 'nos_securitas';
    
    // Energia / Solar - check business area or section name
    if (normalizedArea.includes('energia') || normalizedArea.includes('solar') ||
        normalizedName.includes('energia') || normalizedName.includes('solar') ||
        normalizedName.includes('edp') || normalizedName.includes('iberdrola') ||
        normalizedName.includes('endesa') || normalizedName.includes('galp')) {
      return 'energy';
    }
    
    return null;
  };

  // Get unique operators from selected products
  const getSelectedOperators = (): OperatorType[] => {
    const operators = new Set<OperatorType>();
    const allProducts = [
      ...(saleData.selectedProducts || []),
      ...(saleData.additionalProducts || [])
    ];

    allProducts.forEach(item => {
      const section = sections.find(s => s.id === item.product.section_id);
      if (section) {
        const operator = detectOperatorFromSectionName(section.name, section.business_area);
        if (operator) {
          operators.add(operator);
        }
      }
    });

    return Array.from(operators);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Get operators BEFORE calling onComplete (which might reset state)
      const operators = onOperatorSelected ? getSelectedOperators() : [];
      
      // Save customer info before any state changes
      const customerDataForOperator: OperatorCustomerData = {
        name: saleData.customerInfo?.name || '',
        email: saleData.customerInfo?.email,
        phone: saleData.customerInfo?.phone,
        address: saleData.customerInfo?.address
      };

      let createdLead: Lead | undefined;
      
      // If no existing lead and no duplicate found, create a new lead
      if (!leadId && !duplicateInfo.found && saleData.customerInfo?.name) {
        try {
          const newLeadData = {
            name: saleData.customerInfo.name,
            email: saleData.customerInfo.email || '',
            phone: saleData.customerInfo.phone || undefined,
            address: saleData.customerInfo.address || undefined,
            lead_status: 'Fecho',
            lead_source: 'venda_modal',
            business_area: ['telecomunicacao'],
            customer_type: 'residencial',
            assigned_to: user?.id || undefined
          };
          
          console.log('Creating new lead:', newLeadData);
          createdLead = await apiService.createLead(newLeadData as any);
          console.log('Lead created successfully:', createdLead);
          message.success(`Lead "${createdLead.name}" criado e atribuído a si!`);
        } catch (error) {
          console.error('Error creating lead:', error);
          message.warning('Venda registada, mas houve um erro ao criar o lead.');
        }
      } else if (duplicateInfo.found && duplicateInfo.existingLead) {
        // Use existing lead
        console.log('Using existing lead:', duplicateInfo.existingLead);
        createdLead = duplicateInfo.existingLead;
      }
      
      if (onComplete && saleData.customerInfo) {
        onComplete(saleData as SaleData);
      }
      message.success('Venda registada com sucesso!');
      
      // Close the modal first
      onClose();
      
      // After closing, open operator modals in sequence with a delay
      if (onOperatorSelected && operators.length > 0 && customerDataForOperator.name) {
        console.log('Opening operator modals for:', operators, 'Customer:', customerDataForOperator);
        operators.forEach((operator, index) => {
          setTimeout(() => {
            console.log('Opening modal for operator:', operator);
            onOperatorSelected(operator, customerDataForOperator);
          }, 300 + (index * 600)); // Start after 300ms, then 600ms between each
        });
      }
    } catch (error) {
      console.error('Error completing sale:', error);
      message.error('Erro ao finalizar venda');
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (index: number): 'pending' | 'active' | 'completed' => {
    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'active';
    return 'pending';
  };

  const renderStepContent = () => {
    const stepProps = { data: saleData, updateData };

    switch (currentStep) {
      case 0: return <CustomerInfoStep {...stepProps} />;
      case 1: return <ProductSelectionStep {...stepProps} adjustedCommissions={adjustedCommissions} currentInstalados={currentInstalados} currentInstaladosSeguranca={currentInstaladosSeguranca} segurancaProductsSold={segurancaProductsSold} />;
      case 2: return <RecommendationsStep {...stepProps} />;
      case 3: return <ReviewStep {...stepProps} duplicateInfo={duplicateInfo} />;
      default: return null;
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={resourcesVisible ? 1700 : 1300}
      style={{ top: 20 }}
      className="venda-modal"
      closeIcon={<CloseOutlined />}
      destroyOnClose
      maskClosable={false}
    >
      <div className="venda-layout">
        {/* Sidebar with Steps */}
        <div className="venda-sidebar">
          <div className="venda-sidebar-header">
            <ShoppingCartOutlined />
            <span>Nova Venda</span>
          </div>

          <div className="venda-steps-vertical">
            {steps.map((step, index) => (
              <div
                key={step.key}
                className={`venda-step-item ${getStepStatus(index)}`}
                onClick={() => index < currentStep && setCurrentStep(index)}
                style={{ cursor: index < currentStep ? 'pointer' : 'default' }}
              >
                <div className="venda-step-icon">
                  {getStepStatus(index) === 'completed' ? <CheckOutlined /> : step.icon}
                </div>
                <div className="venda-step-info">
                  <div className="venda-step-title">{step.title}</div>
                  <div className="venda-step-desc">{step.description}</div>
                </div>
              </div>
            ))}
          </div>

          <button
            className={`venda-resource-btn ${resourcesVisible ? 'active' : ''}`}
            onClick={() => setResourcesVisible(!resourcesVisible)}
          >
            <FileTextOutlined />
            <span>Recursos</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="venda-main" style={{ position: 'relative' }}>
          {checkingDuplicates && <DuplicateLoader />}
          <div className="venda-content">
            <div className="venda-step-content">
              {renderStepContent()}
            </div>
          </div>

          {/* Footer */}
          <div className="venda-footer">
            <div className="venda-footer-info">
              {(saleData.totalCommission ?? 0) > 0 && (
                <>Pontos: <strong>{saleData.totalCommission ?? 0}</strong></>
              )}
            </div>
            <div className="venda-footer-actions">
              <Button
                onClick={currentStep === 0 ? onClose : handlePrevious}
                disabled={loading || checkingDuplicates}
                className="venda-btn-secondary"
              >
                {currentStep === 0 ? 'Cancelar' : 'Anterior'}
              </Button>
              <Button
                type="primary"
                onClick={handleNext}
                loading={loading || checkingDuplicates}
                className="venda-btn-primary"
              >
                {currentStep === steps.length - 1 ? 'Confirmar Venda' : 'Continuar'}
              </Button>
            </div>
          </div>
        </div>

        {/* Resources Panel */}
        {resourcesVisible && (
          <div className="venda-resources">
            <div className="venda-resources-title">Recursos de Apoio</div>
            <ProductResourcesViewer />
          </div>
        )}
      </div>
    </Modal>
  );
};

const VendaModal: React.FC<VendaModalProps> = (props) => (
  <SalesProvider>
    <VendaModalContent {...props} />
  </SalesProvider>
);

export default VendaModal;
